import React, {
  useState,
  useEffect,
  useCallback,
  useMemo
} from "react";
import {
  RefreshCw,
  Download,
  Users,
  CircleDollarSign,
  Trash2,
  ArrowUpRight
} from "lucide-react";
import ChannelSendIcons from "../../components/notifications/ChannelSendIcons";
import {
  getDefaulters,
  getDefaultersPortal,
  getDefaulterReport,
  getDefaulterReportPortal,
  exportDefaultersCsv,
  exportDefaultersCsvPortal,
  downloadBlob,
  applyLateFee,
  applyLateFeePortal,
  removeLateFee,
  removeLateFeePortal
} from "../../api/fees/feeApi";
import {
  getAllClasses,
  getAllSections
} from "../../api/student/studentApi";
import {
  getAllClassesForPortal,
  getAllSectionsForPortal
} from "../../api/student/studentPortalApi";
import {
  getAllAcademicYears,
  getAllAcademicYearsPortal,
  getActiveAcademicYear,
  getActiveAcademicYearPortal
} from "../../api/academicyear/academicYearApi";
import {
  Button,
  Select,
  Card,
  CardContent,
  Table,
  Badge,
  EmptyState
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const DueFeesReport = ({
  isPortal = false
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  // CHANGED: academicYearId / classId were raw text inputs expecting a
  // hand-typed ObjectId — not usable in practice. Replaced with dropdowns
  // fed from the same academic-year/class APIs used elsewhere (FeeCollectPage,
  // FeeBatchList). Added sectionId as a new filter, dependent on classId.
  const [filters, setFilters] = useState({
    academicYearId: "",
    classId: "",
    sectionId: ""
  });
  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [applying, setApplying] = useState(null);
  const [removing, setRemoving] = useState(null);
  // Load academic years + classes once, and default to the active academic year
  useEffect(() => {
    (isPortal ? getAllClassesForPortal() : getAllClasses())
    .then((d) => setClasses(d || []))
      .catch(() => {});

    (async () => {
      try {
        const ayRes = isPortal ? await getAllAcademicYearsPortal() : await getAllAcademicYears();
        const years = ayRes?.data || ayRes || [];
        setAcademicYears(Array.isArray(years) ? years : []);
        try {
          const activeRes = isPortal ? await getActiveAcademicYearPortal() : await getActiveAcademicYear();
          const active = activeRes?.data || activeRes;
          if (active?._id) {
            setFilters((p) => ({
              ...p,
              academicYearId: active._id
            }));
          } else {
            const found = (Array.isArray(years) ? years : []).find((y) => y.isActive);
            if (found) setFilters((p) => ({
              ...p,
              academicYearId: found._id
            }));
          }
        } catch {
          const found = (Array.isArray(years) ? years : []).find((y) => y.isActive);
          if (found) setFilters((p) => ({
            ...p,
            academicYearId: found._id
          }));
        }
      } catch {
        /* ignore */
      }
    })();
  }, [isPortal]);
  // Load sections whenever the selected class changes; reset section filter
  // since sections are class-scoped (same pattern as FeeCollectPage).
  useEffect(() => {
    setFilters((p) => ({
      ...p,
      sectionId: ""
    }));
    if (!filters.classId) {
      setSections([]);
      return;
    }
    setSectionsLoading(true);
    (isPortal ? getAllSectionsForPortal(filters.classId) : getAllSections(filters.classId))
    .then((d) => {
        const list = d?.data || d || [];
        setSections(Array.isArray(list) ? list : []);
      })
      .catch(() => setSections([]))
      .finally(() => setSectionsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.classId, isPortal]);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.academicYearId) params.academicYearId = filters.academicYearId;
      if (filters.classId) params.classId = filters.classId;
      // NEW: sectionId now included in the request. NOTE: this requires a
      // backend change — the existing _getDefaulters controller only reads
      // academicYearId/classId from req.query, not sectionId. See message
      // below for the required patch.
      if (filters.sectionId) params.sectionId = filters.sectionId;
      let res;
      try {
        res = isPortal ? await getDefaulterReportPortal(params) : await getDefaulterReport(params);
      } catch {
        res = isPortal ? await getDefaultersPortal(params) : await getDefaulters(params);
      }
      const data = res.data || res || [];
      setRows(Array.isArray(data) ? data : data.rows || data.defaulters || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load defaulters");
    } finally {
      setLoading(false);
    }
  }, [isPortal, filters]);
  const exportCsv = async () => {
    try {
      const params = {};
      if (filters.academicYearId) params.academicYearId = filters.academicYearId;
      if (filters.classId) params.classId = filters.classId;
      if (filters.sectionId) params.sectionId = filters.sectionId;
      const blob = isPortal ?
        await exportDefaultersCsvPortal(params) :
        await exportDefaultersCsv(params);
      downloadBlob(blob, `defaulters-${Date.now()}.csv`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Export failed");
    }
  };
  const applyFine = async (studentId) => {
    if (!studentId) return;
    setApplying(studentId);
    try {
      if (isPortal) await applyLateFeePortal(studentId);
      else await applyLateFee(studentId);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Late fee apply failed");
    } finally {
      setApplying(null);
    }
  };
  const removeFine = async (studentId) => {
    if (!studentId) return;

    setRemoving(studentId);

    try {
      if (isPortal) {
        await removeLateFeePortal(studentId);
      } else {
        await removeLateFee(studentId);
      }

      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Remove late fee failed");
    } finally {
      setRemoving(null);
    }
  };

  // Deep-link into FeeCollectPage for a specific student — bridges the
  // report → collection workflow without merging the two pages.
  const collectLink = (studentId) =>
    isPortal ?
    `/staff-panel/fees/collect?studentId=${studentId}` :
    `/fees/collect?studentId=${studentId}`;

  // ---- Presentation-only derivations below (no data/logic changes) ----

  const academicYearOptions = useMemo(
    () => [{
        label: "All Academic Years",
        value: ""
      },
      ...academicYears.map((y) => ({
        label: `${y.yearName}${y.isActive ? " (Active)" : ""}`,
        value: y._id,
      })),
    ],
    [academicYears]
  );
  const classOptions = useMemo(
    () => [{
        label: "All Classes",
        value: ""
      },
      ...classes.map((c) => ({
        label: `Class ${c.className}`,
        value: c._id
      })),
    ],
    [classes]
  );
  const sectionOptions = useMemo(
    () => [{
        label: sectionsLoading ? "Loading…" : "All Sections",
        value: ""
      },
      ...sections.map((s) => ({
        label: `Section ${s.sectionName || s.name}`,
        value: s._id,
      })),
    ],
    [sections, sectionsLoading]
  );
  // Same field-derivation logic as before, just precomputed once per row
  // so the Table's column renderers stay simple.
  const tableRows = useMemo(() => rows.map((r, idx) => {
    const student = r.student || r.studentId || {};
    const studentId = r.studentId?._id || r.studentId || student._id || r._id;
    const name = r.studentName || [student.firstName, student.lastName].filter(Boolean).join(" ") || "—";
    return {
      id: studentId || idx,
      raw: r,
      student,
      studentId,
      name,
      admissionNo: r.admissionNo || student.admissionNo || "—",
      contact: r.contact || student.primaryContact || "—",
      totalDue: Number(r.totalDue || r.dueAmount || 0),
      totalFine: Number(r.totalFine || r.fineAmount || 0),
      itemCount: r.itemCount || r.count || "—",
    };
  }), [rows]);
  const columns = [{
    key: "name",
    header: "Student",
    render: (row) => <span className="font-medium text-neutral-900 dark:text-neutral-100">{row.name}</span>,
  }, {
    key: "admissionNo",
    header: "Admission"
  }, {
    key: "contact",
    header: "Contact"
  }, {
    key: "totalDue",
    header: "Total Due",
    render: (row) => `₹${row.totalDue.toFixed(2)}`,
  }, {
    key: "totalFine",
    header: "Fine",
    render: (row) => row.totalFine > 0 ? (<Badge variant="warning">₹{row.totalFine.toFixed(2)}</Badge>) : (`₹${row.totalFine.toFixed(2)}`),
  }, {
    key: "itemCount",
    header: "Items"
  }, {
    key: "actions",
    header: "Actions",
    nowrap: true,
    render: (row) => (
      <div className="flex flex-nowrap items-center gap-1.5">
        <Button
          type="button"
          variant="ghost"
          size="xs"
          isLoading={applying === row.studentId}
          leftIcon={<CircleDollarSign className="h-3.5 w-3.5" />}
          onClick={() => applyFine(row.studentId)}
          className="bg-warning-50 text-warning-700 hover:bg-warning-100"
        >
          Apply Fee
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          isLoading={removing === row.studentId}
          leftIcon={<Trash2 className="h-3.5 w-3.5" />}
          onClick={() => removeFine(row.studentId)}
          className="bg-danger-50 text-danger-500 hover:bg-danger-100"
        >
          Remove Fee
        </Button>
        <a
          href={collectLink(row.studentId)}
          className="inline-flex items-center gap-1 rounded bg-primary-50 px-2 py-1 text-xs font-medium text-primary-900 dark:text-primary-300 transition-colors hover:bg-primary-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2"
        >
          Collect <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
        {/* ChannelSendIcons (WA / SMS / Email) */}
        <div className="shrink-0">
          <ChannelSendIcons
            studentId={row.studentId}
            student={row.student}
            isPortal={isPortal}
            defaultEventType="DUE_REMINDER"
          />
        </div>
      </div>
    ),
  },
  ];

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">Due / Defaulters Report</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Outstanding balances and late fee actions</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="primary" leftIcon={<RefreshCw className="h-4 w-4" />} onClick={load}>
            Load
          </Button>
          <Button type="button" variant="secondary" leftIcon={<Download className="h-4 w-4" />} onClick={exportCsv}>
            Export CSV
          </Button>
        </div>
      </div>

      <Card className="mb-4">
        <CardContent className="flex flex-wrap gap-3">
          <div className="w-full sm:w-56">
            <Select
              options={academicYearOptions}
              value={filters.academicYearId}
              onChange={(val) => setFilters((p) => ({ ...p, academicYearId: val }))}
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              options={classOptions}
              value={filters.classId}
              onChange={(val) => setFilters((p) => ({ ...p, classId: val }))}
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              options={sectionOptions}
              value={filters.sectionId}
              onChange={(val) => setFilters((p) => ({ ...p, sectionId: val }))}
              disabled={!filters.classId || sectionsLoading}
            />
          </div>
        </CardContent>
      </Card>

      

      <Table
        columns={columns}
        data={tableRows}
        isLoading={loading}
        density="compact"
        emptyState={
          <EmptyState
            icon={Users}
            title="No defaulters loaded"
            description="Click Load to fetch outstanding balances for the selected filters."
          />
        }
      />
    </div>
  );
};

export default DueFeesReport;
